/**
 * Applicant Form_Renderer — SubmissionErrorSummary
 * (application-form-builder spec, task 23.2).
 *
 * The accessible error summary that blocks submission and names each offending
 * field — most importantly every unanswered required visible Custom_Field
 * (Req 5.3), but also any type/options/limit violation the shared validation
 * surfaces. It follows the WCAG-AA error-summary pattern:
 *   - a `role="alert"` region that receives focus when it appears, so a
 *     screen-reader user is told submission was blocked and why;
 *   - an in-page link per item that moves focus to the offending control, so a
 *     keyboard user can jump straight to the field to fix.
 *
 * The items themselves are computed by `summarizeBlockingErrors`
 * (`validationMessaging.ts`) from the form's React Hook Form errors, which are
 * produced by the shared `deriveZodSchema` / `validateResponse` — so the summary
 * lists exactly the fields the server would reject (Req 5 ↔ Req 7).
 *
 * Validates: Requirements 5.3
 */

import * as React from 'react';

import type { SummaryItem } from './validationMessaging';

interface SubmissionErrorSummaryProps {
  /** The blocking-error rows to list (ordered by the form's display order). */
  items: SummaryItem[];
  /**
   * A monotonically increasing token (e.g. the React Hook Form submit count).
   * When it changes while items are present, the summary re-takes focus so a
   * repeated failed submit re-announces the blocking errors.
   */
  focusToken?: number;
}

/** Move focus to the control named by an error item, falling back to its label. */
function focusField(fieldId: string): void {
  if (typeof document === 'undefined') {
    return;
  }
  const control = document.getElementById(fieldId);
  const target = control ?? document.getElementById(`${fieldId}-label`);
  if (target instanceof HTMLElement) {
    target.focus({ preventScroll: false });
    if (target.scrollIntoView) {
      target.scrollIntoView({ block: 'center' });
    }
  }
}

export function SubmissionErrorSummary({
  items,
  focusToken,
}: SubmissionErrorSummaryProps): React.ReactElement | null {
  const headingRef = React.useRef<HTMLHeadingElement>(null);

  // Re-take focus whenever the summary becomes (or stays) present on a new
  // submit attempt, so the blocking state is announced each time.
  React.useEffect(() => {
    if (items.length > 0 && headingRef.current) {
      headingRef.current.focus();
    }
  }, [focusToken, items.length]);

  if (items.length === 0) {
    return null;
  }

  return (
    <div
      role="alert"
      className="space-y-2 rounded-input border border-destructive-text/40 bg-destructive-text/5 p-4"
    >
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="font-display text-body font-semibold text-destructive-text outline-none"
      >
        Please fix {items.length} {items.length === 1 ? 'item' : 'items'} before submitting.
      </h2>
      <ul className="list-disc space-y-1 pl-5 text-body-sm text-destructive-text">
        {items.map((item) => (
          <li key={item.fieldId}>
            <a
              href={`#${item.fieldId}`}
              className="underline underline-offset-2 hover:no-underline"
              onClick={(e) => {
                e.preventDefault();
                focusField(item.fieldId);
              }}
            >
              {item.label}
            </a>
            : {item.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default SubmissionErrorSummary;
